import { ReactNode, useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

import {
  CircularProgress,
  Page,
  TableContainer,
  TableHeader,
  Box,
  Link,
} from '@occc-app/design-system';
import {
  CalendarGridWeek,
  CalendarGridDayColumn,
  CalendarGridDayTimeLineItem,
  CalendarGridDayOverlappedTimeLineItem,
} from '@occc/ui-design-system';
import { Weekday } from '@occc/graphql';

import { ScheduleEmptyState } from './components';
import { CalendarViewType } from './components/calendar-view-toggle.component';

import {
  SCHEDULE_PAGE_TITLE,
  SCHEDULE_PAGE_SUBTITLE,
  SCHEDULE_CALENDAR_TITLE,
} from '../../touchpoint.constants';
import { KioskSelectionDialog } from '../../components/kiosk-selection-dialog.component';
import { routeCreator } from '../../touchpoint.router';
import { ScheduleItemData } from '../../utils/schedule-mapper';

const MAX_TEXT_LENGTH = 28;

const truncateText = (text: string | undefined): string | undefined => {
  if (!text || text.length <= MAX_TEXT_LENGTH) return text;
  return `${text.slice(0, MAX_TEXT_LENGTH)}...`;
};

type SchedulePageProps = {
  calendarView: CalendarViewType;
  selectedDay: Weekday;
  scheduler?: Partial<Record<Weekday, CalendarGridDayTimeLineItem[]>>;
  loading?: boolean;
  tableActions: ReactNode;
  isKioskDialogOpen: boolean;
  selectedKioskIds: string[];
  onCloseKioskDialog: () => void;
  onConfirmKioskSelection: (kioskIds: string[]) => void;
  onSelectKiosk: () => void;
  onDayItemClick?: (item: CalendarGridDayOverlappedTimeLineItem) => void;
  onPrevDayClick?: () => void;
  onNextDayClick?: () => void;
};

export const SchedulePage = ({
  calendarView,
  selectedDay,
  scheduler,
  loading,
  tableActions,
  onSelectKiosk,
  isKioskDialogOpen,
  onCloseKioskDialog,
  onConfirmKioskSelection,
  selectedKioskIds,
  onDayItemClick,
  onPrevDayClick,
  onNextDayClick,
}: SchedulePageProps) => {
  const showEmptyState = selectedKioskIds.length === 0;

  const dayItems = useMemo(() => {
    return scheduler?.[selectedDay] || [];
  }, [scheduler, selectedDay]);

  const handleOverlappedProps = (items: CalendarGridDayTimeLineItem[]) => ({
    header: `${items.length} Ad blocks`,
    footer: items[0]?.footer,
    subFooter: items[0]?.subFooter,
  });

  const renderAction = useCallback((item: CalendarGridDayTimeLineItem, textColor?: string) => {
    const data = item.data as ScheduleItemData | undefined;

    if (!data?.adSetId) return null;

    return (
      <Link
        variant='caption'
        fontWeight={700}
        component={RouterLink}
        to={routeCreator.adSetsDetails(data.adSetId)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          textDecoration: 'none',
          color: textColor,
          wordBreak: 'break-word',
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {truncateText(data.adSetName)}
        <OpenInNewIcon sx={{ fontSize: 14 }} />
      </Link>
    );
  }, []);

  const renderCalendar = useMemo(() => {
    if (calendarView === 'week') {
      return (
        <CalendarGridWeek
          scheduler={scheduler}
          firstDay={Weekday.Sunday}
          onOverlappedProps={handleOverlappedProps}
          renderAction={renderAction}
        />
      );
    }

    return (
      <CalendarGridDayColumn
        day={selectedDay}
        mode='detailed'
        items={dayItems}
        showTimeColumn
        onClick={onDayItemClick}
        onPrevDayClick={onPrevDayClick}
        onNextDayClick={onNextDayClick}
        onOverlappedProps={handleOverlappedProps}
        renderAction={renderAction}
      />
    );
  }, [
    calendarView,
    scheduler,
    selectedDay,
    dayItems,
    onDayItemClick,
    onPrevDayClick,
    onNextDayClick,
    renderAction,
  ]);

  return (
    <Page
      browserTabTitle={SCHEDULE_PAGE_TITLE}
      pageHeaderProps={{
        title: SCHEDULE_PAGE_TITLE,
        subtitle: SCHEDULE_PAGE_SUBTITLE,
      }}
      containerProps={{ variant: 'lg' }}
    >
      {showEmptyState && <ScheduleEmptyState onClick={onSelectKiosk} />}

      <TableContainer>
        <TableHeader title={SCHEDULE_CALENDAR_TITLE} actions={tableActions} />
        {loading ? (
          <Box display='flex' justifyContent='center' alignItems='center' py={8}>
            <CircularProgress />
          </Box>
        ) : (
          renderCalendar
        )}
      </TableContainer>

      <KioskSelectionDialog
        open={isKioskDialogOpen}
        onClose={onCloseKioskDialog}
        onConfirm={onConfirmKioskSelection}
        initialKioskIds={selectedKioskIds}
      />
    </Page>
  );
};
